import { useCallback, useEffect } from 'react'

function isRefObject<T>(
  ref: React.RefObject<T> | React.ForwardedRef<T>,
): ref is React.RefObject<T> {
  return !!ref && typeof ref !== 'function'
}

export const useViewportIntersection = <T extends HTMLElement>(
  ref: React.RefObject<T> | React.ForwardedRef<T>,
  onEnter?: (observer: IntersectionObserver, observerEntry: IntersectionObserverEntry) => void,
  onExit?: (observer: IntersectionObserver, observerEntry: IntersectionObserverEntry) => void,
  enterThreshold = 1,
) => {
  useEffect(() => {
    if (isRefObject(ref) && ref.current) {
      const { current } = ref
      if ('IntersectionObserver' in window && current) {
        const observer = new IntersectionObserver(
          ([entry], observer) => {
            if (onExit && entry.intersectionRatio <= 0) {
              onExit(observer, entry)
            } else if (onEnter && entry.intersectionRatio >= enterThreshold) {
              onEnter(observer, entry)
            }
          },
          {
            root: null,
            rootMargin: '0px',
            threshold: [0, enterThreshold],
          },
        )

        observer.observe(current)

        return () => {
          observer.unobserve(current)
        }
      }
    }
  }, [ref, onEnter, onExit, enterThreshold])
}

export const useFirstEnterViewport = <T extends HTMLElement>(
  ref: React.RefObject<T> | React.ForwardedRef<T>,
  onFirstEnter: (target: T | null) => void,
  enterThreshold = 1,
) => {
  const onEnter = useCallback(
    (observer: IntersectionObserver) => {
      if (isRefObject(ref) && ref.current) {
        const { current } = ref
        onFirstEnter(current)
        observer.unobserve(current)
      }
    },
    [onFirstEnter, ref],
  )

  useViewportIntersection(ref, onEnter, undefined, enterThreshold)
}
